import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { Search, Star, Clock, Download, Check, X, ArrowRight, Zap, RefreshCw, AlertTriangle, BarChart3, Package } from 'lucide-react';

const ACCENT = '#f97316';

type TemplateCategory = 'sync' | 'migration' | 'automation' | 'alerting' | 'reporting';

interface FlowTemplate {
  id: string;
  name: string;
  description: string;
  category: TemplateCategory;
  tags: string[];
  source_system: string;
  target_products: string[];
  complexity: 'simple' | 'moderate' | 'advanced';
  estimated_setup_minutes: number;
  install_count: number;
  rating: number;
  required_connections: string[];
  template_data: {
    variables: { name: string; label: string; type: string; required: boolean; default_value?: string }[];
  };
  author: string;
  version: string;
}

interface InstalledTemplate {
  id: string;
  template_id: string;
  flow_id: string;
  installed_at: string;
}

const CATEGORIES: { value: string; label: string; icon: React.ReactNode }[] = [
  { value: 'all', label: 'All', icon: <Package size={14} /> },
  { value: 'sync', label: 'Sync', icon: <RefreshCw size={14} /> },
  { value: 'migration', label: 'Migration', icon: <ArrowRight size={14} /> },
  { value: 'automation', label: 'Automation', icon: <Zap size={14} /> },
  { value: 'alerting', label: 'Alerting', icon: <AlertTriangle size={14} /> },
  { value: 'reporting', label: 'Reporting', icon: <BarChart3 size={14} /> },
];

const CATEGORY_COLORS: Record<string, string> = {
  sync: '#3b82f6',
  migration: '#8b5cf6',
  automation: '#f59e0b',
  alerting: '#ef4444',
  reporting: '#10b981',
};

const SOURCE_LABELS: Record<string, string> = {
  connectwise: 'ConnectWise',
  autotask: 'Autotask',
  it_glue: 'IT Glue',
  webhook: 'Webhook',
  bus_event: 'Bus Event',
};

const COMPLEXITY_COLORS: Record<string, string> = {
  simple: '#10b981',
  moderate: '#f59e0b',
  advanced: '#ef4444',
};

function StarRating({ rating }: { rating: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={12}
          fill={i <= Math.round(rating) ? '#f59e0b' : 'none'}
          color={i <= Math.round(rating) ? '#f59e0b' : '#d1d5db'}
        />
      ))}
      <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: 4 }}>{rating.toFixed(1)}</span>
    </span>
  );
}

export default function TemplateGalleryPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<FlowTemplate[]>([]);
  const [installedMap, setInstalledMap] = useState<Record<string, InstalledTemplate>>({});
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<FlowTemplate | null>(null);
  const [installModalOpen, setInstallModalOpen] = useState(false);
  const [installVars, setInstallVars] = useState<Record<string, string>>({});
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    loadTemplates();
    loadInstalled();
  }, [category, search]);

  async function loadTemplates() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== 'all') params.set('category', category);
      if (search) params.set('search', search);
      const res = await api.get<{ templates: FlowTemplate[] }>(`/flow-templates?${params.toString()}`);
      setTemplates(res.templates);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  async function loadInstalled() {
    try {
      const res = await api.get<{ installed_templates: InstalledTemplate[] }>('/installed-templates');
      const map: Record<string, InstalledTemplate> = {};
      for (const it of res.installed_templates) {
        map[it.template_id] = it;
      }
      setInstalledMap(map);
    } catch {
      // ignore
    }
  }

  function openInstallModal(tpl: FlowTemplate) {
    const defaults: Record<string, string> = {};
    for (const v of tpl.template_data.variables) {
      defaults[v.name] = v.default_value || '';
    }
    setInstallVars(defaults);
    setInstallModalOpen(true);
    setSelectedTemplate(tpl);
  }

  async function doInstall() {
    if (!selectedTemplate) return;
    setInstalling(true);
    try {
      const res = await api.post<{ flow: { id: string }; installed_template: InstalledTemplate }>(
        `/flow-templates/${selectedTemplate.id}/install`,
        { variables: installVars },
      );
      setInstalledMap(prev => ({ ...prev, [selectedTemplate.id]: res.installed_template }));
      setInstallModalOpen(false);
      setSelectedTemplate(null);
      // Navigate to the new flow
      navigate(`/flows/${res.flow.id}`);
    } catch {
      // ignore
    } finally {
      setInstalling(false);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Template Gallery</h1>
          <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>
            Pre-built integration templates for common MSP workflows. Install in one click.
          </p>
        </div>
      </div>

      {/* Search + Category Tabs */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 300px' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Search templates..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem 0.5rem 2.25rem',
              border: '1px solid #d1d5db',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
          {CATEGORIES.map(cat => (
            <button
              key={cat.value}
              onClick={() => setCategory(cat.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.375rem 0.75rem',
                borderRadius: '9999px',
                fontSize: '0.8125rem',
                fontWeight: category === cat.value ? 600 : 400,
                border: '1px solid',
                borderColor: category === cat.value ? ACCENT : '#d1d5db',
                backgroundColor: category === cat.value ? ACCENT : '#fff',
                color: category === cat.value ? '#fff' : '#374151',
                cursor: 'pointer',
              }}
            >
              {cat.icon}
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Template Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>Loading templates...</div>
      ) : templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>No templates found.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1rem' }}>
          {templates.map(tpl => {
            const isInstalled = !!installedMap[tpl.id];
            return (
              <div
                key={tpl.id}
                onClick={() => setSelectedTemplate(selectedTemplate?.id === tpl.id ? null : tpl)}
                style={{
                  border: selectedTemplate?.id === tpl.id ? `2px solid ${ACCENT}` : '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  transition: 'box-shadow 0.15s',
                  boxShadow: selectedTemplate?.id === tpl.id ? `0 0 0 1px ${ACCENT}` : undefined,
                }}
              >
                {/* Card Header */}
                <div style={{ padding: '1rem 1rem 0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: 0 }}>{tpl.name}</h3>
                        {isInstalled && (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            fontSize: '0.6875rem', fontWeight: 600, color: '#10b981',
                            backgroundColor: '#ecfdf5', padding: '2px 6px', borderRadius: '4px',
                          }}>
                            <Check size={10} /> Installed
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: '0.8125rem', color: '#6b7280', margin: 0, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {tpl.description}
                      </p>
                    </div>
                  </div>

                  {/* Badges */}
                  <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                    <span style={{
                      fontSize: '0.6875rem', fontWeight: 600,
                      padding: '2px 8px', borderRadius: '4px',
                      backgroundColor: `${CATEGORY_COLORS[tpl.category]}15`,
                      color: CATEGORY_COLORS[tpl.category],
                      textTransform: 'capitalize',
                    }}>
                      {tpl.category}
                    </span>
                    <span style={{
                      fontSize: '0.6875rem', fontWeight: 500,
                      padding: '2px 8px', borderRadius: '4px',
                      backgroundColor: '#f3f4f6', color: '#374151',
                    }}>
                      {SOURCE_LABELS[tpl.source_system] || tpl.source_system}
                    </span>
                    {tpl.target_products.map(p => (
                      <span key={p} style={{
                        fontSize: '0.6875rem', fontWeight: 500,
                        padding: '2px 8px', borderRadius: '4px',
                        backgroundColor: '#eff6ff', color: '#2563eb',
                        textTransform: 'uppercase',
                      }}>
                        {p}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Card Footer */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '0.5rem 1rem', borderTop: '1px solid #f3f4f6', backgroundColor: '#fafafa',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.75rem', color: '#6b7280' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                      color: COMPLEXITY_COLORS[tpl.complexity], fontWeight: 500,
                    }}>
                      {tpl.complexity}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <Clock size={11} /> {tpl.estimated_setup_minutes}m
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <Download size={11} /> {tpl.install_count}
                    </span>
                  </div>
                  <StarRating rating={tpl.rating} />
                </div>

                {/* Expanded Detail */}
                {selectedTemplate?.id === tpl.id && (
                  <div style={{ padding: '1rem', borderTop: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                    <p style={{ fontSize: '0.8125rem', color: '#374151', lineHeight: 1.6, margin: '0 0 0.75rem' }}>
                      {tpl.description}
                    </p>

                    {tpl.required_connections.length > 0 && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <h4 style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', margin: '0 0 0.25rem' }}>Required Connections</h4>
                        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                          {tpl.required_connections.map(c => (
                            <span key={c} style={{
                              fontSize: '0.6875rem', padding: '2px 8px', borderRadius: '4px',
                              backgroundColor: '#fef3c7', color: '#92400e',
                            }}>
                              {c}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {tpl.template_data.variables.length > 0 && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <h4 style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', margin: '0 0 0.25rem' }}>Configuration Variables</h4>
                        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                          {tpl.template_data.variables.map(v => (
                            <span key={v.name} style={{
                              fontSize: '0.6875rem', padding: '2px 8px', borderRadius: '4px',
                              backgroundColor: '#f3f4f6', color: '#374151',
                            }}>
                              {v.label}{v.required ? ' *' : ''}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                      {isInstalled ? (
                        <button
                          onClick={e => { e.stopPropagation(); navigate(`/flows/${installedMap[tpl.id].flow_id}`); }}
                          style={{
                            padding: '0.5rem 1rem', borderRadius: '0.375rem', fontSize: '0.8125rem',
                            fontWeight: 600, border: `1px solid ${ACCENT}`, backgroundColor: '#fff',
                            color: ACCENT, cursor: 'pointer',
                          }}
                        >
                          View Flow
                        </button>
                      ) : (
                        <button
                          onClick={e => { e.stopPropagation(); openInstallModal(tpl); }}
                          style={{
                            padding: '0.5rem 1rem', borderRadius: '0.375rem', fontSize: '0.8125rem',
                            fontWeight: 600, border: 'none', backgroundColor: ACCENT,
                            color: '#fff', cursor: 'pointer',
                          }}
                        >
                          Install Template
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Install Modal */}
      {installModalOpen && selectedTemplate && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setInstallModalOpen(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: '#fff', borderRadius: '0.75rem', padding: '1.5rem',
              width: '100%', maxWidth: 500, maxHeight: '80vh', overflow: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 700, margin: 0 }}>Install: {selectedTemplate.name}</h2>
              <button onClick={() => setInstallModalOpen(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4 }}>
                <X size={18} color="#6b7280" />
              </button>
            </div>

            <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: '1rem' }}>
              Configure the template variables below. Required fields are marked with *.
            </p>

            {selectedTemplate.template_data.variables.map(v => (
              <div key={v.name} style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 500, marginBottom: '0.25rem', color: '#374151' }}>
                  {v.label}{v.required ? ' *' : ''}
                </label>
                <input
                  type="text"
                  value={installVars[v.name] || ''}
                  onChange={e => setInstallVars(prev => ({ ...prev, [v.name]: e.target.value }))}
                  placeholder={v.default_value || ''}
                  style={{
                    width: '100%', padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db', borderRadius: '0.375rem',
                    fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
            ))}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                onClick={() => setInstallModalOpen(false)}
                style={{
                  padding: '0.5rem 1rem', borderRadius: '0.375rem', fontSize: '0.8125rem',
                  fontWeight: 500, border: '1px solid #d1d5db', backgroundColor: '#fff',
                  color: '#374151', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={doInstall}
                disabled={installing}
                style={{
                  padding: '0.5rem 1rem', borderRadius: '0.375rem', fontSize: '0.8125rem',
                  fontWeight: 600, border: 'none', backgroundColor: ACCENT,
                  color: '#fff', cursor: installing ? 'not-allowed' : 'pointer',
                  opacity: installing ? 0.7 : 1,
                }}
              >
                {installing ? 'Installing...' : 'Install & Open Flow'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
